import { getJSON, setJSON, removeJSON } from '@/utils/storage/jsonStorage';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const log = createScopedLogger('CAMPAIGN-CACHE');

export interface CampaignData {
  id: string;
  campaign_id: string;
  country_code: string;
  language_code: string;
  culture?: string | null;
  free_episode_price_coins: number;
  fallback_language_code?: string | null;
  free_episodes_count?: number | null;
  revenue_cat_enabled_app?: boolean;
  revenue_cat_enabled_web?: boolean;
  dcb_enabled_web?: boolean;
}

interface CampaignCacheStore {
  data: Record<string, CampaignData>;
  timestamp: number;
  countryCode: string;
}

const CACHE_KEY = '@campaign_cache';
const CACHE_RETENTION_HOURS = 24;

class PersistentCampaignCacheService {
  private cache: Map<string, CampaignData> = new Map();
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private currentCountry: string | null = null;

  async initialize(countryCode?: string): Promise<void> {
    if (this.initialized && (!countryCode || countryCode === this.currentCountry)) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializeInternal(countryCode);
    return this.initializationPromise;
  }

  private async _initializeInternal(countryCode?: string): Promise<void> {
    const startTime = Date.now();
    log.info('Initializing persistent campaign cache...');

    try {
      const parsed = await getJSON<CampaignCacheStore>(CACHE_KEY);

      if (parsed) {
        const now = Date.now();
        const age = now - parsed.timestamp;
        const maxAge = CACHE_RETENTION_HOURS * 60 * 60 * 1000;

        // Check if cache is valid for this country
        if (countryCode && parsed.countryCode.toLowerCase() !== countryCode.toLowerCase()) {
          log.info(`Country changed (${parsed.countryCode} → ${countryCode}), clearing cache`);
          await this.clear();
        } else if (age > maxAge) {
          log.info(`Cache expired (age: ${Math.round(age / 1000 / 60)}min), clearing`);
          await this.clear();
        } else {
          // Load valid cache
          this.cache = new Map(Object.entries(parsed.data));
          this.currentCountry = parsed.countryCode;
          const duration = Date.now() - startTime;
          log.info(`Loaded ${this.cache.size} entries in ${duration}ms`);
        }
      } else {
        log.info('No cached data found, starting fresh');
      }

      if (countryCode) {
        this.currentCountry = countryCode;
      }

      this.initialized = true;
    } catch (error) {
      log.error('Error initializing cache:', error);
      this.cache.clear();
      this.initialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async persistCache(): Promise<void> {
    if (!this.currentCountry) {
      log.warn('Cannot persist cache without country code');
      return;
    }

    const store: CampaignCacheStore = {
      data: Object.fromEntries(this.cache),
      timestamp: Date.now(),
      countryCode: this.currentCountry,
    };
    const ok = await setJSON(CACHE_KEY, store);
    if (ok) {
      log.info(`Persisted ${this.cache.size} entries`);
    }
  }

  async get(campaignCountriesLanguagesId: string): Promise<CampaignData | null> {
    await this.initialize();
    const data = this.cache.get(campaignCountriesLanguagesId);

    if (data) {
      log.debug(`HIT for ID: ${campaignCountriesLanguagesId}`);
      return data;
    }

    log.debug(`MISS for ID: ${campaignCountriesLanguagesId}`);
    return null;
  }

  async set(data: CampaignData, countryCode?: string): Promise<void> {
    await this.initialize(countryCode);
    this.cache.set(data.id, data);
    await this.persistCache();
  }

  async bulkSet(dataList: CampaignData[], countryCode: string): Promise<void> {
    await this.initialize(countryCode);

    for (const data of dataList) {
      this.cache.set(data.id, data);
    }

    await this.persistCache();
    log.info(`Bulk set ${dataList.length} entries for country: ${countryCode}`);
  }

  async has(campaignCountriesLanguagesId: string): Promise<boolean> {
    await this.initialize();
    return this.cache.has(campaignCountriesLanguagesId);
  }

  async clear(): Promise<void> {
    log.info('Clearing cache');
    this.cache.clear();
    this.currentCountry = null;
    this.initialized = false;
    await removeJSON(CACHE_KEY);
  }

  async getCacheStats(): Promise<{
    size: number;
    initialized: boolean;
    keys: string[];
    countryCode: string | null;
  }> {
    await this.initialize();

    return {
      size: this.cache.size,
      initialized: this.initialized,
      keys: Array.from(this.cache.keys()),
      countryCode: this.currentCountry,
    };
  }

  getCampaignId(campaignCountriesLanguagesId: string): string | null {
    const data = this.cache.get(campaignCountriesLanguagesId);
    return data?.campaign_id || null;
  }

  getEpisodePrice(campaignCountriesLanguagesId: string): number {
    const data = this.cache.get(campaignCountriesLanguagesId);
    return data?.free_episode_price_coins || 0;
  }

  getFallbackLanguages(campaignCountriesLanguagesId: string): string[] {
    const data = this.cache.get(campaignCountriesLanguagesId);
    if (!data?.fallback_language_code) {
      return [];
    }
    return data.fallback_language_code.split(',').map(lang => lang.trim()).filter(lang => lang.length > 0);
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

export const persistentCampaignCache = new PersistentCampaignCacheService();
